import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { FiBell, FiSettings, FiPaperclip } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import RenewalSidebarPanel from './RenewalSidebarPanel';
import GlobalChatBar from '../../components/chat/GlobalChatBar';
import DwAgentChatBar from '../../components/chat/DwAgentChatBar';
import { stripAreaContext } from '../../utils/areaContext';
import type { ChatMessage as ChatMessageType, FileAttachment, MessageNotice, TableData } from './chat.types';
import * as chatService from '../../services/chatService';
import { streamChat, listDwAgents, ARTIFACT_GENERATION_PROMPT, getActiveTurn, reattachChat, interruptChat, isSessionTerminatedError } from '../claudeagents/claudeAgentApi';
import type { SseEvent } from '../claudeagents/claudeAgentApi';
import { proceedPromptFromEvent, type AiQuickPrompt } from '../../utils/proceedPrompt';
import {
  fetchNotifications,
  type NotificationItem,
} from '../../services/dashboardService';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useTypewriter } from '../../hooks/useTypewriter';
import { useAuth } from '../auth/AuthContext';
import { useConversations } from '../../context/ConversationContext';
import { useStatusTemplate } from '../../context/StatusTemplateContext';
import { useAmpsLoaded } from '../../context/AmpContext';
import { useRunDetailPanel } from '../../context/RunDetailPanelContext';
import { useActiveAgent, useAgentReplySignal, type ActiveAgentInfo } from '../../context/ActiveAgentContext';
import { toActiveAgentInfo, colorForAgent, initialsForAgent } from '../../utils/agentAvatar';
import { agentPhotoFor, useAgentPhotosVersion } from '../../utils/agentPhotos';
import { isMeaninglessToolName } from '../../utils/toolHistory';
import logo from '../../assets/images/M_Multicolor.svg';
import TransitionHeatmap from './TransitionHeatmap';
import WidgetTiles from '../widgets/WidgetTiles';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import { fetchConversationFile } from '../../services/historyService';
import '../../styles/ChatContainer.css';

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getTodayLabel(): string {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// Date + time in the browser's local timezone, matching Command Center's fmtCommentDate format.
function formatMsgTimestamp(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${datePart} • ${timePart}`;
}


const WelcomeScreen: React.FC<{ onPromptClick: (prompt: string) => void; userName?: string }> = ({ onPromptClick, userName }) => {
  const displayName = userName || '';
const [notification, setNotification] = useState<NotificationItem | null>(null);
  const [notifLoading, setNotifLoading] = useState(true);

  // Fetch notifications
  useEffect(() => {
    fetchNotifications()
      .then((data) => setNotification(data))
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  }, []);



  return (
    <div className="wv-page">
      {/* ambient glow orbs */}
      <div className="wv-orb wv-orb--purple" />
      <div className="wv-orb wv-orb--cyan" />

      {/* ── Top bar ── */}
      <motion.div className="wv-topbar"
        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="wv-topbar__left">
          <div className="wv-topbar__logo">
            <img src={logo} alt="" />
            <div className="wv-topbar__logo-ring" />
          </div>
          <div>
            <h1 className="wv-topbar__greeting">
              {getGreeting()}{displayName ? `, ${displayName}` : ''}
            </h1>
            <p className="wv-topbar__date">{getTodayLabel()}</p>
          </div>
        </div>
        <div className="wv-topbar__badge">
          <span className="wv-topbar__badge-dot" />
          <RiSparklingFill size={12} />
          Ask me anything
        </div>
      </motion.div>

      {/* ── Body: left + right ── */}
      <div className="wv-body">

        {/* ── Left column ── */}
        <div className="wv-left">

          {/* System message */}
          <motion.div className="wv-system"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}>
            <div className="wv-system__avatar-wrap">
              <div className="wv-system__avatar-pulse" />
              <div className="wv-system__avatar">
                <RiSparklingFill size={16} />
              </div>
            </div>
            <div className="wv-system__bubble">
              {notifLoading ? (
                <div className="wv-system__skel">
                  <div className="chat-skel-bone wv-system__skel-line" style={{ width: '30%' }} />
                  <div className="chat-skel-bone wv-system__skel-line" style={{ width: '94%' }} />
                  <div className="chat-skel-bone wv-system__skel-line" style={{ width: '88%' }} />
                  <div className="chat-skel-bone wv-system__skel-line" style={{ width: '60%' }} />
                </div>
              ) : (
                <>
                  <div className="wv-system__name-row">
                    <span className="wv-system__name">{notification?.title}</span>
                    <span className="wv-system__live">● live</span>
                  </div>
                  <div className="wv-system__text">
                    {notification?.message ? <ReactMarkdown>{notification.message}</ReactMarkdown> : null}
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Notifications */}
          {/* <motion.div className="wv-section"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}>
            <div className="wv-section__header">
              <span className="wv-section__header-icon"><FiBell size={12} /></span>
              <span>Alerts & Updates</span>
              <span className="wv-section__badge">{notifications.length}</span>
            </div>
            <div className="wv-notifications">
              {notifications.map((n, i) => (
                <motion.button key={n.id} className="wv-notif"
                  onClick={() => onPromptClick(n.prompt)}
                  style={{ '--notif-color': n.color } as React.CSSProperties}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.26 + i * 0.07, duration: 0.28 }}
                  whileHover={{ scale: 1.015, x: 2 }} whileTap={{ scale: 0.99 }}>
                  <span className="wv-notif__icon-circle">{n.icon}</span>
                  <span className="wv-notif__body">
                    <span className="wv-notif__title">{n.title}</span>
                    <span className="wv-notif__desc">{n.desc}</span>
                  </span>
                  <FiArrowRight size={13} className="wv-notif__arrow" />
                </motion.button>
              ))}
            </div>
          </motion.div> */}
          <motion.div className="wv-section"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}>
            <div className="wv-section__header">
              <span className="wv-section__header-icon"><FiBell size={12} /></span>
              <span>Alerts & Updates</span>
            </div>
           <div className="wv-alerts-empty">
              <FiBell size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
              <span>No alerts right now</span>
            </div>
          </motion.div>

          <WidgetTiles />

          <TransitionHeatmap />

        </div>


      </div>
    </div>
  );
};

function generateConversationId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function extractToolHistoryMetadata(content: string):
  | { aiToolName: string[]; aiToolPayload: string[]; aiToolResponse: string[] }
  | undefined {
  const tryParse = (raw: string): any | null => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const extractEmbeddedJson = (text: string): string | null => {
    const keyIdx = text.indexOf('"type"');
    if (keyIdx === -1) return null;
    const start = text.lastIndexOf('{', keyIdx);
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (ch === '\\') {
          escaped = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
    return null;
  };

  const trimmed = (content || '').trim();
  if (!trimmed) return undefined;

  let parsed = tryParse(trimmed);
  if (!parsed) {
    const fenced = trimmed.match(/```json\s*([\s\S]*?)```/i)?.[1];
    if (fenced) parsed = tryParse(fenced.trim());
  }
  if (!parsed) {
    const embedded = extractEmbeddedJson(trimmed);
    if (embedded) parsed = tryParse(embedded);
  }
  if (!parsed || parsed.type !== 'tool_history' || !Array.isArray(parsed.tool_history)) {
    return undefined;
  }

  const names: string[] = [];
  const payloads: string[] = [];
  const responses: string[] = [];

  for (const t of parsed.tool_history) {
    names.push(String(t?.tool_name ?? 'tool'));
    payloads.push(typeof t?.tool_payload === 'string' ? t.tool_payload : JSON.stringify(t?.tool_payload ?? ''));
    responses.push(typeof t?.tool_response === 'string' ? t.tool_response : JSON.stringify(t?.tool_response ?? ''));
  }

  return names.length > 0
    ? { aiToolName: names, aiToolPayload: payloads, aiToolResponse: responses }
    : undefined;
}

function parseMarkdownTable(content: string): TableData | null {
  const lines = content.split('\n');
  const tableLines: string[] = [];
  let capturing = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|')) { capturing = true; tableLines.push(trimmed); }
    else if (capturing) break;
  }
  if (tableLines.length < 2) return null;
  const dataLines = tableLines.filter(l => !/^\|[\s:|-]+\|$/.test(l));
  if (dataLines.length < 2) return null;
  const parseRow = (line: string) => line.split('|').slice(1, -1).map(c => c.trim());
  return { headers: parseRow(dataLines[0]), rows: dataLines.slice(1).map(parseRow) };
}

function extractFilesFromMessageContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const lines = (content || '').split('\n');
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inAttachedSection) break;
      continue;
    }
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      continue;
    }
    if (!inAttachedSection) continue;

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      rows.push({ name: withUrl[1].trim(), fileUrl: withUrl[2].trim(), size: 0, type: '' });
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (!urlOnly) continue;

    const url = urlOnly[1].trim();
    const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
    rows.push({ name: decodeURIComponent(rawName), fileUrl: url, size: 0, type: '' });
  }

  return rows;
}

// Markdown links pointing at the portal artifact viewer (…/artifacts/{guid}).
// Older assistant turns embedded these inline in the answer text — extract them
// as attachments so history-loaded messages still surface generated files.
const ARTIFACT_LINK_RE = /\[([^\]]*)\]\((https?:\/\/[^()\s]*\/artifacts\/[^()\s]+)\)/g;

function extractArtifactLinksFromContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const re = new RegExp(ARTIFACT_LINK_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(content || '')) !== null) {
    const url = m[2].trim();
    const fallbackName = decodeURIComponent(url.split('?')[0].split('/').pop() || '') || 'Generated file';
    rows.push({ name: (m[1] || '').trim() || fallbackName, fileUrl: url, size: 0, type: '' });
  }
  return rows;
}

// Artifact viewer URLs (…/artifacts/{guid}) are full portal pages — open them
// in a new tab instead of nesting the whole app inside the preview modal's
// iframe. Uploaded files are plain blob SAS URLs and keep the inline preview.
function isArtifactViewerUrl(url: string): boolean {
  return /\/artifacts\//i.test(url);
}

// Paced thought rendering. The backend streams the agent's running commentary
// as word-level chunks (see _to_support_chat_sse in claudeManaged_module.py),
// and a whole burst of them — one tool announcement is ~25 words — lands in a
// single network chunk. Appending them all inside one synchronous onEvent pass
// would batch into a single render, so the thinking appears as a finished block
// instead of typing itself out. Draining a queue on a timer restores the
// token-style effect the kanban stream already has (CommandIssuePanel.tsx).
const THOUGHT_TICK_MS = 16;
// Ticks a backlog is allowed to take, so a large burst still drains in roughly
// THOUGHT_TICK_MS * THOUGHT_DRAIN_TICKS (~0.6s) rather than one word per tick.
const THOUGHT_DRAIN_TICKS = 40;

interface ChatContainerProps {
  onCommissionScan?: (pdf: FileAttachment, table: TableData, conversationId: string) => void;
  onResetScan?: () => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ onCommissionScan, onResetScan }) => {
  const { conversationId: routeConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const userName = user?.name || [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') || localStorage.getItem('userName') || '';
  const { getThread, createThread, addMessageToThread, setThreadAgent, loadConversation, touchThread } = useConversations();
  const { setStatusTemplate } = useStatusTemplate();
  const { amps } = useAmpsLoaded();
  const amsCopilotAmp = amps.find(a => a.name === 'AMS Copilot');
  const { setRunDetailPanel } = useRunDetailPanel();
  // Clear the auto-opened run-details panel when leaving chat entirely, so it
  // doesn't linger in the sidebar on an unrelated page.
  useEffect(() => () => setRunDetailPanel(null), [setRunDetailPanel]);


  const [conversationId, setConversationId] = useState<string>(() => {
    return routeConversationId || generateConversationId();
  });

  const prevRouteIdRef = useRef<string | undefined>(routeConversationId);
  // Id this page just generated for a brand-new chat — it has no history yet,
  // so landing on its URL must not fetch (and show skeletons for) one.
  const freshConvIdRef = useRef<string | null>(null);
  
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [thoughtText, setThoughtText] = useState('');
  const [currentTool, setCurrentTool] = useState<string | undefined>();
  const [isThinking, setIsThinking] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [lastStreamChunkAt, setLastStreamChunkAt] = useState(0);
  const [showStreamGapSkeleton, setShowStreamGapSkeleton] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(() => !!routeConversationId);
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [scanPdfAttachment, setScanPdfAttachment] = useState<FileAttachment | null>(null);
  const [scanTable, setScanTable] = useState<TableData | null>(null);
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string } | null>(null);
  // Index of the top-bar file chip currently being resolved through the proxy.
  const [loadingTopFileIndex, setLoadingTopFileIndex] = useState<number | null>(null);
  const [dwAgent, setDwAgent] = useState<{ id: string; name: string } | null>(null);
  // Server-issued session id for the claude-managed /chat stream — only set
  // once the DW agent's first message gets a session.start event back; null
  // means "no session yet", which streamChat() takes as "start a new one".
  const [dwSessionId, setDwSessionId] = useState<string | null>(null);
  // Digital Workforce's primary agent — the default "workspace" a plain New Chat
  // (no specific agent picked via Start Chat) talks to. Fetched once; dwAgent
  // being null always means "no agent explicitly chosen", so effectiveDwAgent
  // below falls back to this rather than a generic non-agent chat.
  const [primaryAgent, setPrimaryAgent] = useState<{ id: string; name: string } | null>(null);
  // The active roster, drawn the same way the global chat bar draws it — so a
  // New Chat opened for a specific agent can point that bar at them.
  const [agentOptions, setAgentOptions] = useState<ActiveAgentInfo[]>([]);
  // Until the roster answers, a new chat doesn't know whether it's the agent
  // home or the plain welcome screen — it shows neither (no greeting flash).
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  useEffect(() => {
    listDwAgents(false, true)
      .then((records) => {
        const primary = records.find((r) => r.is_primary_agent && !r.is_archived);
        if (primary) setPrimaryAgent({ id: primary.agent_id, name: primary.name });
        setAgentOptions(records.filter((r) => !r.is_archived).map(toActiveAgentInfo));
      })
      .catch(() => {})
      .finally(() => setAgentsLoaded(true));
  }, []);
  // Whoever the app-wide chat bar is currently talking to.
  const { activeAgent, setActiveAgent } = useActiveAgent();
  useAgentPhotosVersion();
  const effectiveDwAgent = dwAgent ?? primaryAgent;
  // consumeDwStream's handleStreamEvent closure (AI-112 Part 2, artifact.created
  // handling) needs the CURRENT agent, not the one captured when that
  // useCallback was created — its dependency array is deliberately just
  // [addMessageToThread] (see its own definition below), so a plain
  // `effectiveDwAgent` reference inside it would be permanently stale at
  // whatever this was on first render (null, before dwAgent/primaryAgent's
  // async fetches resolve). Same ref-for-a-stale-closure pattern as
  // activeConvRef below.
  const effectiveDwAgentRef = useRef(effectiveDwAgent);
  useEffect(() => { effectiveDwAgentRef.current = effectiveDwAgent; }, [effectiveDwAgent]);
  const isInitializedRef = useRef(false);
  const pendingAutoSendRef = useRef('');
  const hasPendingNavigateRef = useRef(false);
  const handleSendRef = useRef<((content: string, attachments?: FileAttachment[]) => void) | null>(null);
  // Re-attach to a turn still generating server-side after a page refresh —
  // ref-based like handleSendRef so the route-change effect (declared above
  // the callback) can call the latest version.
  const tryReattachRef = useRef<((convId: string) => void) | null>(null);
  const reattachAbortRef = useRef<AbortController | null>(null);
  // Set when the user presses Stop (POST /chat/interrupt) for the in-flight
  // turn; read once by consumeDwStream when that turn's stream ends so the
  // partial answer is finished with a "stopped" notice instead of an error.
  const interruptRequestedRef = useRef(false);
  const reattachingConvRef = useRef<string | null>(null);

  const displayedContent = useTypewriter(streamingContent, streamingContent.length > 0);
  const scrollRef = useAutoScroll(messages, displayedContent || thoughtText);

  useEffect(() => {
    if (!streamingContent) {
      setShowStreamGapSkeleton(false);
      return;
    }

    const tick = () => {
      const staleMs = Date.now() - lastStreamChunkAt;
      setShowStreamGapSkeleton(!isThinking && staleMs >= 1200);
    };

    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [streamingContent, lastStreamChunkAt, isThinking]);
  const activeConvRef = useRef<string>(conversationId);
  // Keep activeConvRef in sync
  useEffect(() => {
    activeConvRef.current = conversationId;
  }, [conversationId]);

  // "New Chat" button dispatches this event when already on a /chat/* route
  useEffect(() => {
    const handleNewChat = () => {
      const newId = generateConversationId();
      freshConvIdRef.current = newId;
      hasPendingNavigateRef.current = true;
      setConversationId(newId);
      activeConvRef.current = newId;
      setMessages([]);
      setThoughtText('');
      setCurrentTool(undefined);
      setIsThinking(false);
      setStreamingContent('');
      setScanPdfAttachment(null);
      setScanTable(null);
      onResetScan?.();
      setIsLoadingHistory(false);
      setStatusTemplate(null);
      setDwAgent(null);
      setDwSessionId(null);
      isInitializedRef.current = true;
      navigate(`/chat/${newId}`, { replace: true, state: null });
    };
    window.addEventListener('newChatConversation', handleNewChat);
    return () => window.removeEventListener('newChatConversation', handleNewChat);
  }, [navigate, onResetScan, setStatusTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle initial mount and route changes
  useEffect(() => {
    const prevRouteId = prevRouteIdRef.current;
    prevRouteIdRef.current = routeConversationId;
    const navState = location.state as {
      initialMessage?: string;
      dwAgentId?: string;
      dwAgentName?: string;
      dwSessionId?: string;
      forceHistoryRefresh?: boolean;
    } | null;
    const forceHistoryRefresh = !!navState?.forceHistoryRefresh;

    // Once we have a real route ID, reset the pending-navigate guard
    if (routeConversationId) hasPendingNavigateRef.current = false;

    // Case 1: No route ID (new chat clicked) — guard prevents double-fire
    if (!routeConversationId) {
      if (hasPendingNavigateRef.current) return;
      hasPendingNavigateRef.current = true;
      const newId = generateConversationId();
      freshConvIdRef.current = newId;
      setConversationId(newId);
      activeConvRef.current = newId;
      setMessages([]);
      setThoughtText('');
      setCurrentTool(undefined);
      setIsThinking(false);
      setStreamingContent('');
      setScanPdfAttachment(null);
      setScanTable(null);
      onResetScan?.();
      setIsLoadingHistory(false);
      setStatusTemplate(null);
      setDwAgent(null);
      setDwSessionId(null);
      isInitializedRef.current = true;
      navigate(`/chat/${newId}`, { replace: true, state: null });
      return;
    }

    // Case 2: Route ID exists and changed (switching conversations)
    if (routeConversationId !== prevRouteId || !isInitializedRef.current || forceHistoryRefresh) {
      // Just the URL catching up with the new chat Case 1 / New Chat created:
      // state is already reset and there's no history to load.
      if (routeConversationId === freshConvIdRef.current && !navState?.initialMessage && !navState?.dwAgentId && !forceHistoryRefresh) {
        freshConvIdRef.current = null;
        isInitializedRef.current = true;
        return;
      }
      freshConvIdRef.current = null;
      const isFirstConversationLoad = !isInitializedRef.current;
      const shouldForceHistoryRefresh = forceHistoryRefresh || isFirstConversationLoad;
      // Mark initialized immediately so unstable context refs can't re-trigger this block
      isInitializedRef.current = true;

      setConversationId(routeConversationId);
      activeConvRef.current = routeConversationId;
      // Leaving a conversation kills its passive reattach viewer — the turn
      // itself keeps running server-side and lands in history when done.
      reattachAbortRef.current?.abort();
      // Hide welcome immediately while switching threads.
      setMessages([]);
      setIsLoadingHistory(true);
      setThoughtText('');
      setCurrentTool(undefined);
      setIsThinking(false);
      setStreamingContent('');
      setStatusTemplate(null);
      if (prevRouteId !== undefined) {
        setPendingPrompt('');
      }

        // Capture any pending auto-send message (from persistent chat input on other pages)
      const initialMessage = navState?.initialMessage;
      const pendingMsg = initialMessage || pendingAutoSendRef.current;
      pendingAutoSendRef.current = '';
      setDwAgent(navState?.dwAgentId && navState?.dwAgentName ? { id: navState.dwAgentId, name: navState.dwAgentName } : null);
      setDwSessionId(navState?.dwSessionId ?? null);
      if (initialMessage || navState?.dwAgentId || shouldForceHistoryRefresh) {
        navigate(`/chat/${routeConversationId}`, { replace: true, state: null });
      }

      // dwAgent/dwSessionId (set just above) are enough on their own to route
      // follow-up messages correctly (see handleSend) — transcript loading for
      // a Digital Workforce-originated conversation uses the exact same
      // momentum-my-amp-history/{conversationId} lookup as any other thread
      // (via loadConversation → historyService.fetchConversationDetail), not
      // the claude-managed sessions/{sessionId}/messages endpoint.

      // Load messages for this conversation, then fire auto-send if pending
      const thread = getThread(routeConversationId);
      if (!shouldForceHistoryRefresh && thread && thread.messages.length > 0) {
        setMessages(thread.messages);
        setIsLoadingHistory(false);
        // Shown from memory, so no history fetch stamps it as opened — record
        // the access ourselves (history lists order by it). A chat about to
        // send a message gets bumped by that turn instead.
        if (pendingMsg) handleSendRef.current?.(pendingMsg);
        else touchThread(routeConversationId);
      } else {
        setIsLoadingHistory(true);
        loadConversation(routeConversationId, shouldForceHistoryRefresh)
          .then((loaded) => {
            if (activeConvRef.current === routeConversationId) {
              setMessages(loaded?.messages ?? []);
              if (pendingMsg) handleSendRef.current?.(pendingMsg);
              // A turn sent before the refresh may still be generating
              // server-side — it won't be in history yet, so check for it and
              // re-attach to its live stream.
              else tryReattachRef.current?.(routeConversationId);
            }
          })
          .catch(() => {
            if (activeConvRef.current === routeConversationId) {
              setMessages([]);
            }
          })
          .finally(() => {
            if (activeConvRef.current === routeConversationId) {
              setIsLoadingHistory(false);
            }
          });
      }
    }
  }, [routeConversationId, navigate, getThread, loadConversation, touchThread, location.state, onResetScan, setStatusTemplate]);

  // Shared consumer for a claude-managed SSE turn — used by both a live send
  // (streamChat) and a post-refresh re-attach (reattachChat). Accumulates
  // streamed text / tool calls / artifacts / follow-up suggestions and appends
  // the final assistant message to the conversation when the stream ends.
  // `run` starts the stream and resolves when it closes. With
  // suppressEmptyFinal, a stream that produced nothing (e.g. a reattach that
  // lost the race with the finishing turn) just clears the indicators instead
  // of rendering an error bubble.
  const consumeDwStream = useCallback(async (
    run: (onEvent: (e: SseEvent) => void) => Promise<void>,
    opts?: { suppressEmptyFinal?: boolean },
  ): Promise<void> => {
    // The chat this turn belongs to. The user can leave it while the answer is
    // still streaming (New Chat, the logo, another conversation) — from then on
    // the turn must not draw into the chat now on screen; its answer is still
    // saved to its own conversation below.
    const turnConvId = activeConvRef.current;
    const onTurnChat = () => activeConvRef.current === turnConvId;
    const view = {
      setThoughtText: (v: React.SetStateAction<string>) => { if (onTurnChat()) setThoughtText(v); },
      setIsThinking: (v: boolean) => { if (onTurnChat()) setIsThinking(v); },
      setCurrentTool: (v: string | undefined) => { if (onTurnChat()) setCurrentTool(v); },
      setLastStreamChunkAt: (v: number) => { if (onTurnChat()) setLastStreamChunkAt(v); },
      setShowStreamGapSkeleton: (v: boolean) => { if (onTurnChat()) setShowStreamGapSkeleton(v); },
      setStreamingContent: (v: string) => { if (onTurnChat()) setStreamingContent(v); },
      setMessages: (v: React.SetStateAction<ChatMessageType[]>) => { if (onTurnChat()) setMessages(v); },
    };
    let fullText = '';
    let answerChatId: string | undefined;
    const streamedToolNames: string[] = [];
    const streamedToolPayloads: string[] = [];
    const streamedToolResponses: string[] = [];
    // Files the agent generated this turn (artifact.created events) —
    // attached to the final assistant message so AttachmentList and the
    // sticky top Files bar render them as clickable chips.
    const streamedArtifacts: FileAttachment[] = [];
    // Artifact-export flag from the terminal answer event — drives the
    // "Generate artifact" button in the response's actions row.
    let canGenerateArtifact = false;
    // Backend go-ahead decision from the terminal answer event — a prompt,
    // null for "no buttons", undefined when the backend sent no decision.
    // ChatMessage renders one-click Yes / No replies from it.
    let proceedPrompt: AiQuickPrompt | null | undefined;
    // Why the turn ended early, if it did (fatal error / terminated session /
    // stopped by the user) — rendered by ChatMessage as a banner under the
    // answer rather than being appended to the answer text.
    let turnNotice: MessageNotice | undefined;

    // ── Thought stream ────────────────────────────────────────────────────────
    // `thoughtBody` is the authoritative accumulated text; queued chunks are
    // committed to state a few at a time by the drain timer below.
    const thoughtQueue: string[] = [];
    let thoughtBody = '';
    let drainTimer: ReturnType<typeof setInterval> | null = null;
    const stopThoughtDrain = () => {
      if (drainTimer !== null) {
        clearInterval(drainTimer);
        drainTimer = null;
      }
    };
    const startThoughtDrain = () => {
      if (drainTimer !== null) return;
      drainTimer = setInterval(() => {
        if (thoughtQueue.length === 0) {
          stopThoughtDrain();
          return;
        }
        const perTick = Math.max(1, Math.ceil(thoughtQueue.length / THOUGHT_DRAIN_TICKS));
        thoughtBody += thoughtQueue.splice(0, perTick).join('');
        view.setThoughtText(thoughtBody);
      }, THOUGHT_TICK_MS);
    };
    // Commit everything still queued at once — used when the answer starts
    // streaming (the thinking is done, don't let it trail the response) and on
    // every exit path so no chunk is silently dropped.
    const flushThoughts = () => {
      stopThoughtDrain();
      if (thoughtQueue.length === 0) return;
      thoughtBody += thoughtQueue.splice(0).join('');
      view.setThoughtText(thoughtBody);
    };
    let sawRealThought = false;
    const enqueueThought = (chunk: string) => {
      sawRealThought = true;
      thoughtQueue.push(chunk);
      startThoughtDrain();
    };
    // Marker so the ThinkingBlock indicator renders (and stays visible for the
    // rest of the turn) on streams that emit no `thought` events — e.g. the
    // Approve/Deny resume path — instead of only the one-shot "Thinking..."
    // bubble that disappears the moment text starts streaming. Skipped once
    // real thought chunks are in flight, so it never flashes ahead of them.
    const markThinkingFallback = () => {
      view.setIsThinking(true);
      if (sawRealThought) return;
      view.setThoughtText(t => t || 'Thinking through your request…');
    };

    const handleStreamEvent = (evt: SseEvent) => {
      if (evt.type === 'thought') {
        // Real running commentary from the backend — tool announcements,
        // lifecycle notes and the model's own thinking, already chunked.
        view.setIsThinking(true);
        if (evt.thought) enqueueThought(evt.thought);
        if (evt.currentTool) view.setCurrentTool(evt.currentTool);
      } else if (evt.type === 'agent.thinking' || evt.type === 'span.model_request_start') {
        markThinkingFallback();
      } else if (evt.type === 'agent.mcp_tool_use' || evt.type === 'agent.tool_use' || evt.type === 'agent.custom_tool_use') {
        markThinkingFallback();
        view.setCurrentTool(evt.name);
        streamedToolNames.push(String(evt.name || 'tool'));
        streamedToolPayloads.push(JSON.stringify(evt.input ?? {}));
        streamedToolResponses.push('');
      } else if (evt.type === 'agent.message' || evt.type === 'assistant.message' || evt.type === 'message.delta') {
        const chunk = (evt.content ?? []).filter(c => c.type === 'text').map(c => c.text ?? '').join('');
        if (chunk) {
          fullText += chunk;
          flushThoughts();
          view.setIsThinking(false);
          view.setLastStreamChunkAt(Date.now());
          view.setShowStreamGapSkeleton(false);
          view.setStreamingContent(fullText);
        }
      } else if (evt.type === 'artifact.created') {
        if (evt.app_url) {
          const fallbackName = decodeURIComponent(evt.app_url.split('?')[0].split('/').pop() || '') || 'Generated file';
          streamedArtifacts.push({ name: evt.filename || fallbackName, fileUrl: evt.app_url, size: 0, type: '' });
        } else if (evt.artifact_id) {
          // AI-112 Part 2: a Hermes artifact has no Momentum-portal app_url —
          // it's fetched via MyAMP's own download proxy instead (keeps the
          // Hermes key server-side). openAttachment below opens this as a
          // bare `window.open(url)` with no custom header (it matches
          // isArtifactViewerUrl's `/artifacts/` check), so the auth token
          // rides along as a query param rather than an Authorization header
          // — the download endpoint accepts either.
          const dwAgentId = effectiveDwAgentRef.current?.id;
          if (dwAgentId) {
            const authToken = localStorage.getItem('authToken') || '';
            const url = `${process.env.REACT_APP_MyAMP_Url ?? ''}/api/v1/claude-managed/hermes/${dwAgentId}/artifacts/${evt.artifact_id}/download?token=${encodeURIComponent(authToken)}`;
            streamedArtifacts.push({ name: evt.filename || 'Generated file', fileUrl: url, size: 0, type: '' });
          }
        }
      } else if (evt.type === 'error') {
        // Fatal turn error — e.g. BUDGET_REACHED (the session hit its spending
        // budget and was paused) or SESSION_TERMINATED (the AI session is gone;
        // the next message starts a fresh one). Whatever answer text already
        // streamed is kept; the reason becomes a notice banner under it so a
        // turn cut off mid-answer explains why it stopped instead of silently
        // ending early — and never reads as part of the answer.
        turnNotice = {
          kind: isSessionTerminatedError(evt) ? 'terminated' : 'error',
          text: evt.error,
          code: evt.code,
        };
      } else if (evt.type === 'answer') {
        // AMS-style terminal/partial answer — the /claude-managed/chat multipart
        // contract replies in this shape, not agent.message. Partial events have
        // responseStreaming !== false; the terminal event (responseStreaming ===
        // false) exists to carry tool_history/chatId but its response field still
        // holds the LAST streamed chunk — appending it would duplicate the whole
        // answer. Only consume the terminal response when nothing was streamed
        // (e.g. error-only turns).
        const isTerminal = evt.responseStreaming === false;
        if (evt.response && (!isTerminal || !fullText)) {
          fullText += evt.response;
          flushThoughts();
          const sepIdx = fullText.indexOf('\n\n');
          if (sepIdx >= 15) {
            const before = fullText.substring(0, sepIdx);
            const after = fullText.substring(sepIdx + 2);
            const checkLen = Math.min(after.length, before.length, 30);
            if (checkLen >= 15 && after.startsWith(before.substring(0, checkLen))) {
              fullText = after;
            }
          }
          view.setIsThinking(false);
          view.setLastStreamChunkAt(Date.now());
          view.setShowStreamGapSkeleton(false);
          view.setStreamingContent(fullText);
        }
        if (evt.chatId) answerChatId = evt.chatId;
        if (isTerminal) {
          canGenerateArtifact = !!evt.can_generate_artifact;
          proceedPrompt = proceedPromptFromEvent(evt);
        }
        // Terminal answer carries the authoritative artifacts list — merge
        // (deduped by URL) with any artifact.created events streamed above.
        for (const art of evt.artifacts ?? []) {
          if (!art.app_url || streamedArtifacts.some(f => f.fileUrl === art.app_url)) continue;
          const fallbackName = decodeURIComponent(art.app_url.split('?')[0].split('/').pop() || '') || 'Generated file';
          streamedArtifacts.push({ name: art.filename || fallbackName, fileUrl: art.app_url, size: 0, type: '' });
        }
        if (evt.tool_history?.length) {
          // Full name+payload+response per call — replaces the partial names-only
          // data collected from streamed tool_use events above.
          streamedToolNames.length = 0;
          streamedToolPayloads.length = 0;
          streamedToolResponses.length = 0;
          evt.tool_history.forEach((t) => {
            if (isMeaninglessToolName(t.tool_name)) return;
            streamedToolNames.push(t.tool_name);
            streamedToolPayloads.push(t.tool_payload ?? '');
            streamedToolResponses.push(t.tool_response ?? '');
          });
        } else if (evt.ai_tool?.length && streamedToolNames.length === 0) {
          evt.ai_tool.forEach((name) => {
            if (isMeaninglessToolName(name)) return;
            streamedToolNames.push(name);
            streamedToolPayloads.push('');
            streamedToolResponses.push('');
          });
        }
      }
    };

    try {
      await run(handleStreamEvent);
    } finally {
      // The drain timer outlives `run()` whenever the last thought chunks are
      // still queued — never leave it ticking against a finished turn (it would
      // keep writing into the next turn's ThinkingBlock).
      stopThoughtDrain();
    }

    // The user pressed Stop this turn: the session ran to a safe boundary and
    // went idle, so whatever text arrived is the whole answer. The backend's
    // "empty response" error for a turn stopped before any text is not a
    // failure here — only a real termination outranks the stopped notice.
    if (interruptRequestedRef.current) {
      interruptRequestedRef.current = false;
      if (!turnNotice || turnNotice.kind !== 'terminated') {
        turnNotice = {
          kind: 'stopped',
          text: fullText.trim() ? 'Response stopped.' : 'Response stopped before any text was generated.',
        };
      }
    }

    if (opts?.suppressEmptyFinal && !fullText.trim() && streamedToolNames.length === 0 && streamedArtifacts.length === 0 && !turnNotice) {
      view.setStreamingContent('');
      view.setIsThinking(false);
      view.setThoughtText('');
      view.setCurrentTool(undefined);
      return;
    }

    const parsedToolMeta = extractToolHistoryMetadata(fullText || '');
    const streamedToolMeta = streamedToolNames.length > 0
      ? { aiToolName: streamedToolNames, aiToolPayload: streamedToolPayloads, aiToolResponse: streamedToolResponses }
      : undefined;
    const toolMeta = parsedToolMeta ?? streamedToolMeta;
    const finalMeta = (toolMeta || answerChatId || streamedArtifacts.length > 0 || canGenerateArtifact || proceedPrompt !== undefined || turnNotice)
      ? {
          ...toolMeta,
          ...(answerChatId ? { chatId: answerChatId } : {}),
          ...(streamedArtifacts.length > 0 ? { attachments: streamedArtifacts } : {}),
          ...(canGenerateArtifact ? { canGenerateArtifact: true } : {}),
          ...(proceedPrompt !== undefined ? { proceedPrompt } : {}),
          ...(turnNotice ? { notice: turnNotice } : {}),
        }
      : undefined;
    const finalMsg: ChatMessageType = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      // An error-only or stopped-before-text turn keeps an empty body: the
      // notice banner carries the explanation, so no placeholder text is
      // needed (and none would be copied as if it were an answer).
      content: fullText || (turnNotice ? '' : "I couldn't process that request. Please try again."),
      timestamp: formatMsgTimestamp(new Date()),
      ...(finalMeta ? { metadata: finalMeta } : {}),
    };
    view.setStreamingContent('');
    view.setLastStreamChunkAt(0);
    view.setShowStreamGapSkeleton(false);
    view.setIsThinking(false);
    view.setThoughtText('');
    view.setCurrentTool(undefined);
    view.setMessages((prev) => [...prev, finalMsg]);
    addMessageToThread(turnConvId, finalMsg);
  }, [addMessageToThread]);

  // Check whether a turn is still generating server-side for this conversation
  // (sent before a page refresh) and, if so, render its pending question and
  // stream the rest of the turn live through the shared consumer.
  const tryReattach = useCallback(async (convId: string) => {
    if (reattachingConvRef.current === convId) return;
    reattachingConvRef.current = convId;
    try {
      const info = await getActiveTurn(convId);
      if (!info?.active || activeConvRef.current !== convId) return;
      // The stored question is the raw prompt sent to the agent — strip the
      // attachment appendix the backend adds for file turns.
      const question = stripAreaContext((info.question || '').split('\n\n[Attached files]')[0]).trim();
      if (question) {
        const userMsg: ChatMessageType = {
          id: `user-reattach-${Date.now()}`,
          role: 'user',
          content: question,
          timestamp: formatMsgTimestamp(new Date()),
        };
        if (!getThread(convId)) {
          createThread(userMsg, convId);
        } else {
          addMessageToThread(convId, userMsg);
        }
        setMessages((prev) => [...prev, userMsg]);
      }
      setIsThinking(true);
      setThoughtText(t => t || 'Reconnecting to your running request…');
      const controller = new AbortController();
      reattachAbortRef.current = controller;
      await consumeDwStream(
        (onEvent) => reattachChat(convId, (evt) => {
          // Conversation switched while re-attached — stop mirroring; the turn
          // itself keeps running server-side and persists to history.
          if (activeConvRef.current !== convId) {
            controller.abort();
            return;
          }
          onEvent(evt);
        }, controller.signal),
        { suppressEmptyFinal: true },
      );
    } catch {
      if (activeConvRef.current === convId) {
        setIsThinking(false);
        setStreamingContent('');
        setThoughtText('');
        setCurrentTool(undefined);
      }
    } finally {
      if (reattachingConvRef.current === convId) reattachingConvRef.current = null;
    }
  }, [consumeDwStream, getThread, createThread, addMessageToThread]);
  useEffect(() => { tryReattachRef.current = tryReattach; }, [tryReattach]);

  const handleSend = useCallback(async (content: string, attachments?: FileAttachment[], apiPrompt?: string) => {
    // Only show non-silent attachments in the chat UI; silent ones are background API context
    const displayAttachments = attachments?.filter(a => !a.silent);
    if (displayAttachments?.length) {
      setScanPdfAttachment(displayAttachments[0]);
      setScanTable(null);
      onResetScan?.();
    }
    const userMsg: ChatMessageType = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: formatMsgTimestamp(new Date()),
      ...(displayAttachments && displayAttachments.length > 0 ? { metadata: { attachments: displayAttachments } } : {}),
    };

    // Use activeConvRef so we always have the current conversationId without
    // making handleSend depend on conversationId state (which would cause
    // the auto-send to fire prematurely when Case 2 updates conversationId).
    const convId = activeConvRef.current;
    const existingThread = getThread(convId);
    if (!existingThread) {
      createThread(userMsg, convId);
    } else {
      addMessageToThread(convId, userMsg);
    }
    // Tag the thread with the assigned Digital Workforce agent so reopening
    // it later (e.g. from the sidebar's Chat History) re-assigns the same
    // agent instead of falling back to the default AMS copilot — otherwise
    // this info only lives in ChatContainer's own dwAgent state and is lost
    // the moment the thread is looked up from a different entry point.
    if (effectiveDwAgent) {
      setThreadAgent(convId, effectiveDwAgent.id, effectiveDwAgent.name);
    }

    setMessages((prev) => [...prev, userMsg]);

    setIsThinking(true);
    setThoughtText('');
    setCurrentTool(undefined);
    setStreamingContent('');
    setLastStreamChunkAt(0);
    setShowStreamGapSkeleton(false);

    try {
      // Extract file paths from attachments
      const filePaths = attachments
        ?.filter(att => !!att.fileUrl)
        .map(att => ({ fileName: att.name, fileURL: att.fileUrl! })) || [];

      if (effectiveDwAgent) {
        // Coming from Digital Workforce (or falling back to the primary agent) —
        // talk to the claude-managed chat stream directly rather than the AMS
        // channel-chat endpoint. Event handling and final-message assembly live
        // in consumeDwStream (shared with the post-refresh re-attach flow).
        const fileUrls = filePaths.map(f => f.fileURL);
        const fileNames = filePaths.map(f => f.fileName);
        const ncToken = localStorage.getItem('amsToken') || undefined;
        const rateToken = localStorage.getItem('rateToken') || undefined;
        const nowcertsAmsUrl = localStorage.getItem('agencyDomain') || undefined;

        // Prefer a resumed DW session id (from Digital Workforce history) when we
        // have one; otherwise this chat's own conversationId — already known from
        // the URL on the very first message — becomes the conversationId sent to
        // the API on every turn. dwSessionId is deliberately never updated from
        // incoming events here: it must stay stable for the life of this chat, or
        // message #2 would silently start sending a different id than message #1.
        await consumeDwStream((onEvent) =>
          streamChat(
            apiPrompt ?? content,
            dwSessionId ?? convId,
            onEvent,
            undefined,
            content,
            fileUrls.length ? fileUrls : undefined,
            fileNames.length ? fileNames : undefined,
            ncToken,
            effectiveDwAgent.id,
            { rateToken, nowcertsAmsUrl },
          )
        );
        return;
      }

      const chatUrl = amsCopilotAmp?.chatUrl || '/chat';
      const ampId = amsCopilotAmp?.id;
      const lastImageUrl = chatService.getLastImageUrl(messages);

      // Same as consumeDwStream: only draw while this chat is still on screen.
      const onSendChat = () => activeConvRef.current === convId;
      await chatService.sendMessage(apiPrompt ?? content, chatUrl, {
        onThought: (thoughtSoFar, tool) => {
          if (!onSendChat()) return;
          setIsThinking(true);
          setThoughtText(thoughtSoFar);
          if (tool) setCurrentTool(tool);
        },
        onChunk: (textSoFar) => {
          if (!onSendChat()) return;
          setIsThinking(false);
          setLastStreamChunkAt(Date.now());
          setShowStreamGapSkeleton(false);
          setStreamingContent(textSoFar);
        },
        onDone: (finalMsg) => {
          addMessageToThread(convId, finalMsg);
          if (!onSendChat()) return;
          setStreamingContent('');
          setLastStreamChunkAt(0);
          setShowStreamGapSkeleton(false);
          setIsThinking(false);
          setThoughtText('');
          setCurrentTool(undefined);
          setMessages((prev) => [...prev, finalMsg]);
          const parsed = parseMarkdownTable(finalMsg.content);
          if (parsed) setScanTable(parsed);
        },
      }, convId, filePaths, ampId, undefined, lastImageUrl);
    } catch {
      if (activeConvRef.current !== convId) return;
      setIsThinking(false);
      setStreamingContent('');
      setLastStreamChunkAt(0);
      setShowStreamGapSkeleton(false);
      setThoughtText('');
    }
  }, [createThread, addMessageToThread, setThreadAgent, getThread, amsCopilotAmp, messages, effectiveDwAgent, dwSessionId, consumeDwStream]);

  // Keep handleSendRef current so loadConversation.then() always calls the latest version
  useEffect(() => { handleSendRef.current = handleSend; }, [handleSend]);

  const handlePromptClick = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
  }, []);

  const showWelcome = messages.length === 0 && !isThinking;

  // The agent's face for its replies: uploaded logo, else built-in photo,
  // else colour + initials — the same as everywhere else in the app.
  const replyAgent = effectiveDwAgent ? (() => {
    const known = agentOptions.find((a) => a.id === effectiveDwAgent.id);
    const name = known?.name ?? effectiveDwAgent.name;
    return {
      name,
      photo: agentPhotoFor(effectiveDwAgent.id) ?? known?.avatarPhoto,
      color: known?.avatarColor ?? colorForAgent(effectiveDwAgent.id),
      initials: known?.avatarInitials ?? initialsForAgent(name),
    };
  })() : null;

  // A brand-new, empty chat is the app-wide chat bar opened full-size (see
  // the render below), so it talks to whoever that bar is on. A New Chat
  // opened for a specific agent (e.g. the channel agent dropdown) points the
  // bar at them first. Deliberately keyed on dwAgent only: switching agent in
  // the bar afterwards must not be pulled back to this one.
  const isNewChat = showWelcome && !isLoadingHistory && !dwSessionId;
  useEffect(() => {
    if (!isNewChat || !dwAgent || dwAgent.id === activeAgent?.id) return;
    const requested = agentOptions.find((a) => a.id === dwAgent.id);
    if (requested) setActiveAgent(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewChat, dwAgent?.id, agentOptions]);

  // The New Chat page's bar hands its first message here rather than running
  // its own quick chat, so the conversation starts in the full chat (tool
  // steps, approvals, artifacts). Point this chat at the bar's agent first,
  // then send once that agent is the one handleSend will use.
  const [pendingHandoff, setPendingHandoff] = useState<{ text: string; apiText: string; files: FileAttachment[]; agentId: string } | null>(null);
  const handleHomeSubmit = useCallback((text: string, files: { name: string; url: string }[], agent: ActiveAgentInfo, apiText: string) => {
    setDwAgent({ id: agent.id, name: agent.name });
    setPendingHandoff({
      text: text || 'See attached file(s).',
      // @-linked records travel to the agent as structured tags; the chat
      // itself shows the plain text you typed.
      apiText: apiText || text || 'See attached file(s).',
      files: files.map((f) => ({ name: f.name, size: 0, type: '', fileUrl: f.url })),
      agentId: agent.id,
    });
  }, []);
  // An open agent conversation uses the same chat bar as New Chat, handing
  // each message to handleSend instead of the bar's own quick chat.
  const handleBarSubmit = useCallback((text: string, files: { name: string; url: string }[], _mentions: unknown, apiText: string) => {
    const body = text || 'See attached file(s).';
    const attachments: FileAttachment[] = files.map((f) => ({ name: f.name, size: 0, type: '', fileUrl: f.url }));
    const tagged = apiText || body;
    void handleSend(body, attachments.length ? attachments : undefined, tagged !== body ? tagged : undefined);
  }, [handleSend]);
  useEffect(() => {
    if (!pendingHandoff || effectiveDwAgent?.id !== pendingHandoff.agentId) return;
    const { text, apiText, files } = pendingHandoff;
    setPendingHandoff(null);
    void handleSend(text, files.length ? files : undefined, apiText !== text ? apiText : undefined);
  }, [pendingHandoff, effectiveDwAgent, handleSend]);

  // Tell anything watching (e.g. a Digital Team strip) this agent is replying.
  useAgentReplySignal(effectiveDwAgent?.id, isThinking || streamingContent.length > 0);
  const isStreaming = isThinking || streamingContent.length > 0;

  // Stop the agent's in-flight turn. The backend sends user.interrupt to the
  // session; the open stream then ends normally and consumeDwStream renders
  // the partial answer with a "stopped" notice instead of an error. Only the
  // Digital Workforce (claude-managed) stream is stoppable this way — the
  // legacy AMS copilot path has no server-side turn to interrupt.
  const handleInterrupt = useCallback(async () => {
    const target = dwSessionId ?? activeConvRef.current;
    if (!target || !isStreaming || !effectiveDwAgent) return;
    interruptRequestedRef.current = true;
    try {
      await interruptChat(target);
    } catch (err) {
      // Usually a 409: the turn finished before the click landed, so the
      // stream will close on its own — leave the notice logic alone.
      interruptRequestedRef.current = false;
      console.warn('interruptChat failed', err);
    }
  }, [dwSessionId, isStreaming, effectiveDwAgent]);

  const hasActiveStream = thoughtText || streamingContent;
  const topAttachments = React.useMemo(() => {
    const seen = new Set<string>();
    const rows: FileAttachment[] = [];
    for (const msg of messages) {
      const attachments = [
        ...(msg.metadata?.attachments ?? []),
        ...extractFilesFromMessageContent(msg.content),
        ...extractArtifactLinksFromContent(msg.content),
      ];
      for (const att of attachments) {
        const key = (att.fileUrl || att.preview || att.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        rows.push(att);
      }
    }
    return rows;
  }, [messages]);

  // New chat, roster still loading: an empty page, not the greeting screen
  // that the agent home would replace a moment later.
  if (isNewChat && !agentsLoaded && !pendingHandoff) {
    return (
      <div className="chat-layout">
        <div className="chat-main chat-main--agent-home" />
        <RenewalSidebarPanel />
      </div>
    );
  }

  if (isNewChat && agentOptions.length > 0 && !pendingHandoff) {
    return (
      <div className="chat-layout">
        <div className="chat-main chat-main--agent-home">
          {/* Keyed per conversation so New Chat always starts the bar fresh. */}
          <GlobalChatBar key={conversationId} variant="page" onSubmit={handleHomeSubmit} />
        </div>
        <RenewalSidebarPanel />
      </div>
    );
  }

  return (
    <div className="chat-layout">
      {previewFile && (
        <FilePreviewModal
          name={previewFile.name}
          url={previewFile.url}
          onClose={() => {
            if (previewFile.url.startsWith('blob:')) URL.revokeObjectURL(previewFile.url);
            setPreviewFile(null);
          }}
        />
      )}
      <div className="chat-main">
        <div className={`chat-container${topAttachments.length > 0 ? ' chat-container--has-top-files' : ''}`} ref={scrollRef}>
          {isLoadingHistory ? (
            <div className="chat-container__messages">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`chat-skel-${i}`} className="chat-msg-skel">
                  <div className="chat-skel-bone chat-msg-skel__avatar" />
                  <div className="chat-msg-skel__body">
                    <div className="chat-skel-bone chat-msg-skel__line" style={{ width: '30%' }} />
                    <div className="chat-skel-bone chat-msg-skel__bubble" />
                  </div>
                </div>
              ))}
            </div>
          ) : showWelcome ? (
            <WelcomeScreen onPromptClick={handlePromptClick} userName={userName} />
          ) : (
            <div className="chat-container__messages">
              {topAttachments.length > 0 && (
                <>
                <div className="chat-top-files-bar">
                <div className="chat-top-files-row">
                  <span className="chat-top-files-label">
                    Files
                  </span>
                  {topAttachments.map((file, idx) => {
                    const fileLink = file.fileUrl || file.preview;
                    const displayName = decodeURIComponent((file.name || '').replace(/\+/g, ' '))
                      .replace(/\s*,\s*/g, ', ')
                      .replace(/\s+/g, ' ')
                      .trim();
                    const canOpen = !!fileLink;
                    return (
                      <button
                        key={`chat-top-file-${displayName}-${idx}`}
                        type="button"
                        disabled={!canOpen}
                        title={displayName}
                        onClick={async () => {
                          if (!fileLink) return;
                          if (isArtifactViewerUrl(fileLink)) {
                            window.open(fileLink, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          // The SAS URL stored in `fileUrl` expires, so resolve a
                          // fresh one through the authenticated file endpoint
                          // (same path the per-message chips take) and only fall
                          // back to the stored link if the endpoint has nothing.
                          if (conversationId && file.name) {
                            setLoadingTopFileIndex(idx);
                            const proxied = await fetchConversationFile(conversationId, file.name);
                            setLoadingTopFileIndex(null);
                            setPreviewFile({ name: displayName, url: proxied || fileLink });
                            return;
                          }
                          setPreviewFile({ name: displayName, url: fileLink });
                        }}
                        style={{
                          cursor: canOpen ? 'pointer' : 'default',
                          opacity: canOpen ? (loadingTopFileIndex === idx ? 0.5 : 1) : 0.65,
                        }}
                        className="chat-top-file-chip"
                      >
                        <FiPaperclip size={11} />
                        <span className="chat-top-file-chip__name">{displayName}</span>
                      </button>
                    );
                  })}
                </div>
                </div>
                </>
              )}
              {messages.map((msg, idx) => (
                <ChatMessage
                  key={msg.id}
                  agent={replyAgent}
                  message={msg}
                  chatId={conversationId}
                  isLatest={idx === messages.length - 1}
                  onStartNewChat={
                    // The AI session ended on the service side: offer the same
                    // fresh start as the sidebar's "New Chat" (it listens for
                    // this event while a /chat/* route is open).
                    msg.metadata?.notice?.kind === 'terminated'
                      ? () => window.dispatchEvent(new CustomEvent('newChatConversation'))
                      : undefined
                  }
                  onGenerateArtifact={
                    idx === messages.length - 1 && !hasActiveStream && !isThinking
                      ? () => handleSend(
                          'Generate a downloadable file from your last response',
                          undefined,
                          ARTIFACT_GENERATION_PROMPT,
                        )
                      : undefined
                  }
                  onQuickReply={
                    idx === messages.length - 1 && msg.role === 'assistant' && !hasActiveStream && !isThinking
                      ? handleSend
                      : undefined
                  }
                  // A carrier choice card (several policies) sends the person's pick as their next message.
                  onChoose={!hasActiveStream && !isThinking ? (text: string) => handleSend(text) : undefined}
                />
              ))}

              {isThinking && !thoughtText && !streamingContent && (
                <motion.div
                  className="chat-msg chat-msg--assistant chat-msg--thinking"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="chat-msg__header">
                    <div className="chat-msg__avatar">
                      <div className="chat-msg__avatar-ai">
                        <RiSparklingFill size={14} />
                      </div>
                    </div>
                    <span className="chat-msg__sender">Insurance Agency AI</span>
                  </div>
                  <div className="chat-msg__body">
                    <div className="thinking-indicator">
                      <div className="thinking-indicator__shimmer" />
                      <span className="thinking-indicator__text">
                        <RiSparklingFill size={12} className="thinking-indicator__icon" />
                        Thinking...
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}

              {hasActiveStream && (
                <ChatMessage
                  agent={replyAgent}
                  message={{
                    id: 'streaming',
                    role: 'assistant',
                    content: displayedContent,
                    isStreaming: true,
                    timestamp: formatMsgTimestamp(new Date()),
                    metadata: {
                      // model: 'Sonnet 4.5',
                      ...(thoughtText ? { thoughtText } : {}),
                      ...(currentTool ? { currentTool } : {}),
                      isThinkingNow: isThinking,
                    },
                  }}
                  showStreamingGapIndicator={showStreamGapSkeleton}
                />
              )}
            </div>
          )}
        </div>

        {scanPdfAttachment && !isThinking && !streamingContent && (
          <div className="amp-advanced-bar">
            <motion.button
              className="amp-advanced-btn"
              onClick={() => onCommissionScan?.(scanPdfAttachment, scanTable ?? { headers: [], rows: [] }, conversationId)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiSettings size={13} />
              Advanced Options
            </motion.button>
          </div>
        )}

        {effectiveDwAgent && replyAgent ? (
          <div className="global-chat-bar-dock">
            <DwAgentChatBar
              key={effectiveDwAgent.id}
              agentId={effectiveDwAgent.id}
              agentName={replyAgent.name}
              avatarColor={replyAgent.color}
              avatarInitials={replyAgent.initials}
              avatarPhoto={replyAgent.photo}
              onSubmit={handleBarSubmit}
              externalBusy={isStreaming}
              onInterrupt={handleInterrupt}
            />
          </div>
        ) : (
          <div className="chat-input-area">
            <ChatInput
              onSend={handleSend}
              disabled={isStreaming}
              placeholder={!isAuthenticated ? "Try asking a question..." : undefined}
              externalValue={pendingPrompt}
              conversationId={conversationId}
              selectedAmpId={amsCopilotAmp?.id}
              overrideAmpName={effectiveDwAgent?.name}
              overrideAgentId={effectiveDwAgent?.id}
            />
          </div>
        )}
      </div>

      <RenewalSidebarPanel />
    </div>
  );
};

export default ChatContainer;
